<template>
  <div>
    <header class="global-navigation flex justify-between">
      <NavigationLogo :to="isSimpleHeader ? '' : '/account/seekers/dashboard'" />
      <router-link
        v-if="!basicAccount && !isSimpleHeader"
        to="/messages"
        class="flex mobile-messaging-icon mr-16"
        tabindex="0"
        :aria-current="activeIcon === 'messages' && 'page'"
        aria-label="Messages"
        role="link"
      >
        <messages-icon-outline />
        <badge v-if="unreadMessageCount > 0" :number="unreadMessageCount" />
      </router-link>
      <button
        v-else-if="isSimpleHeader"
        id="mobile-account-menu-btn"
        class="flex flex-row justify-center items-center md-1:hidden h-full mr-16"
        tabindex="0"
        :aria-expanded="isModalOpen ? 'true' : 'false'"
        aria-haspopup="global-nav-dropdown"
        @click="toggleModal"
      >
        <Avatar :name="`${account.firstName} ${account.lastName}`" :imageUrl="avatarUrl" />
        <div class="text-caption -mb-8 ml-6">
          <span class="text-caption truncate-name">{{ account.firstName }}</span>
          <caret-icon class="inline mb-8" :class="isModalOpen && 'upside-down-icon'" />
        </div>
      </button>
      <Button
        v-else
        class="mr-16 md-1:hidden"
        value="Upgrade"
        variant="primary"
        href="/upgrade/pricing"
        size="small"
        @click="upgradeClicked"
      />
      <nav class="flex h-full items-center mr-24 icon-container hidden" aria-label="Primary Navigation">
        <router-link
          v-if="!isSimpleHeader"
          to="/account/seekers/dashboard"
          class="nav-icon-group h-full justify-center flex flex-col items-center"
          :class="activeIcon === 'home' && 'active'"
          :aria-current="activeIcon === 'home' && 'page'"
          tabindex="0"
          role="link"
        >
          <home-icon-filled v-if="activeIcon === 'home'" />
          <home-icon-outline v-else />
          <p class="text-caption">Home</p>
        </router-link>
        <router-link
          v-if="!isSimpleHeader"
          class="nav-icon-group h-full justify-center flex flex-col items-center"
          :class="activeIcon === 'jobs' && 'active'"
          :aria-current="activeIcon === 'jobs' && 'page'"
          tabindex="0"
          role="link"
          to="/seeker/jobs/open"
        >
          <div class="relative">
            <badge v-if="receivedAppsCount > 0" :number="receivedAppsCount" class="absolute -top-6 left-12" />
            <jobs-icon-filled v-if="activeIcon === 'jobs'" />
            <jobs-icon-outline v-else />
          </div>
          <p class="text-caption">Jobs</p>
        </router-link>
        <router-link
          v-if="!isSimpleHeader"
          to="/availability/search"
          class="nav-icon-group h-full justify-center flex flex-col items-center"
          :class="activeIcon === 'explore' && 'active'"
          :aria-current="activeIcon === 'explore' && 'page'"
          tabindex="0"
          role="link"
        >
          <magnifying-glass-icon-filled v-if="activeIcon === 'explore'" />
          <magnifying-glass-icon-outline v-else />
          <p class="text-caption">Explore</p>
        </router-link>
        <router-link
          v-if="!basicAccount && !isSimpleHeader"
          to="/messages"
          class="nav-icon-group h-full justify-center flex flex-col items-center"
          :class="activeIcon === 'messages' && 'active'"
          :aria-current="activeIcon === 'messages' && 'page'"
          tabindex="0"
          role="link"
        >
          <div class="relative">
            <badge v-if="unreadMessageCount > 0" :number="unreadMessageCount" class="absolute -top-6 left-12" />
            <messages-icon-filled v-if="activeIcon === 'messages'" />
            <messages-icon-outline v-else />
          </div>
          <p class="text-caption">Messages</p>
        </router-link>
        <router-link
          v-if="!basicAccount && !isSimpleHeader"
          to="/bookings"
          class="nav-icon-group h-full justify-center flex flex-col items-center"
          :class="activeIcon === 'bookings' && 'active'"
          :aria-current="activeIcon === 'bookings' && 'page'"
          tabindex="0"
          role="link"
        >
          <bookings-icon-filled v-if="activeIcon === 'bookings'" />
          <bookings-icon-outline v-else />
          <p class="text-caption">Bookings</p>
        </router-link>
        <button
          id="desktop-account-menu-btn"
          class="nav-icon-group h-full justify-center flex flex-col items-center"
          tabindex="0"
          :aria-expanded="isModalOpen ? 'true' : 'false'"
          aria-haspopup="global-nav-dropdown"
          :aria-label="`Account Menu. You are logged in as ${account.firstName}.`"
          @click="toggleModal"
        >
          <Avatar :name="`${account.firstName} ${account.lastName}`" :imageUrl="avatarUrl" />
          <div class="text-caption -mb-8">
            <span id="desktop-account-menu-name" class="text-caption truncate-name">{{ account.firstName }}</span>
            <caret-icon class="inline mb-8" :class="isModalOpen && 'upside-down-icon'" aria-hidden="true" />
          </div>
        </button>
        <Button
          v-if="!isSimpleHeader && basicAccount"
          value="Upgrade"
          variant="primary"
          href="/upgrade/pricing"
          size="medium"
          @click="upgradeClicked"
        />
      </nav>
    </header>
    <global-nav-dropdown
      v-show="isModalOpen"
      :firstName="account.firstName"
      :lastName="account.lastName"
      :profileImage="avatarUrl"
      :accountType="accountType"
      :isSimpleHeader="isSimpleHeader"
      @handleClickOutside="handleClickOutside"
    />
    <nav v-if="!isSimpleHeader" id="mobile-nav-bar" class="flex h-full items-center icon-container-mobile-bottom">
      <router-link
        to="/account/seekers/dashboard"
        class="nav-icon-group mobile h-full justify-center flex flex-col items-center"
        :class="activeIcon === 'home' && 'active'"
        :aria-current="activeIcon === 'home' && 'page'"
        role="link"
        tabindex="0"
      >
        <home-icon-filled v-if="activeIcon === 'home'" />
        <home-icon-outline v-else />
        <p class="text-caption">Home</p>
      </router-link>
      <router-link
        to="/seeker/jobs/open"
        class="nav-icon-group mobile h-full justify-center flex flex-col items-center"
        :class="activeIcon === 'jobs' && 'active'"
        :aria-current="activeIcon === 'jobs' && 'page'"
        role="link"
        tabindex="0"
      >
        <badge v-if="receivedAppsCount > 0" :number="receivedAppsCount" class="absolute top-3.5 mb-32 lg:mb-4 ml-20" />
        <jobs-icon-filled v-if="activeIcon === 'jobs'" />
        <jobs-icon-outline v-else />
        <p class="text-caption">Jobs</p>
      </router-link>
      <router-link
        to="/availability/search"
        class="nav-icon-group mobile h-full justify-center flex flex-col items-center"
        :class="activeIcon === 'explore' && 'active'"
        :aria-current="activeIcon === 'explore' && 'page'"
        role="link"
        tabindex="0"
      >
        <magnifying-glass-icon-filled v-if="activeIcon === 'explore'" />
        <magnifying-glass-icon-outline v-else />
        <p class="text-caption">Explore</p>
      </router-link>
      <router-link
        v-if="!basicAccount"
        to="/bookings"
        class="nav-icon-group mobile h-full justify-center flex flex-col items-center"
        :class="activeIcon === 'bookings' && 'active'"
        :aria-current="activeIcon === 'bookings' && 'page'"
        role="link"
        tabindex="0"
      >
        <bookings-icon-filled v-if="activeIcon === 'bookings'" />
        <bookings-icon-outline v-else />
        <p class="text-caption">Bookings</p>
      </router-link>
      <button
        id="mobile-account-menu-btn"
        class="nav-icon-group mobile h-full justify-center flex flex-col items-center"
        tabindex="0"
        :aria-expanded="isModalOpen ? 'true' : 'false'"
        @click="toggleModal"
      >
        <Avatar :name="`${account.firstName} ${account.lastName}`" :imageUrl="avatarUrl" />
        <p class="text-caption truncate-name">{{ account.firstName }}</p>
      </button>
    </nav>
  </div>
</template>

<script lang="ts">
// Lib
import startCase from 'lodash/startCase';
import { sendSegmentEvent } from '@/lib/segment';

// Icons
import HomeIconFilled from '@/icons/global_nav/HomeIconFilled.vue';
import HomeIconOutline from '@/icons/global_nav/HomeIconOutline.vue';
import JobsIconFilled from '@/icons/global_nav/JobsIconFilled.vue';
import JobsIconOutline from '@/icons/global_nav/JobsIconOutline.vue';
import MagnifyingGlassIconFilled from '@/icons/global_nav/MagnifyingGlassIconFilled.vue';
import MagnifyingGlassIconOutline from '@/icons/global_nav/MagnifyingGlassIconOutline.vue';
import MessagesIconFilled from '@/icons/global_nav/MessagesIconFilled.vue';
import MessagesIconOutline from '@/icons/global_nav/MessagesIconOutline.vue';
import BookingsIconFilled from '@/icons/global_nav/BookingsIconFilled.vue';
import BookingsIconOutline from '@/icons/global_nav/BookingsIconOutline.vue';
import CaretIcon from '@/icons/global_nav/CaretIcon.vue';

// Components
import Avatar from '../avatar/Avatar.vue';
import Badge from '../badge/Badge.vue';
import Button from '../button/Button.vue';
import GlobalNavDropdown from './GlobalNavDropdown.vue';
import NavigationLogo from './NavigationLogo.vue';

export default {
  name: 'GlobalNav',
  components: {
    // Icons
    HomeIconFilled,
    HomeIconOutline,
    JobsIconFilled,
    JobsIconOutline,
    MagnifyingGlassIconOutline,
    MagnifyingGlassIconFilled,
    MessagesIconFilled,
    MessagesIconOutline,
    BookingsIconFilled,
    BookingsIconOutline,
    CaretIcon,

    // Components
    Avatar,
    Badge,
    Button,
    GlobalNavDropdown,
    NavigationLogo,
  },
  data(): any {
    return {
      isModalOpen: false,
      handleEscapeKey: null as any,
    };
  },
  computed: {
    unreadMessageCount(): number {
      return this.$store.getters['navigation/unreadMessageCount'];
    },
    avatarUrl(): string {
      return this.account?.croppedPhoto || this.account?.originalPhoto || '';
    },
    account() {
      return this.$store.getters['account/getAccount'];
    },
    receivedAppsCount(): number {
      return this.$store.getters['navigation/receivedAppsCount'];
    },
    activeIcon() {
      const path = this.$route?.path ?? window.location.pathname;
      if (path === '/account/seekers/dashboard') return 'home';
      if (path.startsWith('/seeker/jobs')) return 'jobs';
      if (path === '/messages') return 'messages';
      if (path === '/bookings' || path.startsWith('/bookings/')) return 'bookings';
      if (path === '/profile/provider/search' || path === '/availability/search') return 'explore';
      return '';
    },
    basicAccount(): boolean {
      return this.account?.isBasic();
    },
    accountType(): string {
      return startCase(this.account.accountType());
    },
    isSimpleHeader(): boolean {
      return this.$route.matched.some((r) => r.meta?.simpleHeader);
    },
  },
  watch: {
    isModalOpen(val) {
      if (val) {
        const dropdown = document.getElementById('global-nav-dropdown');
        this.$nextTick(() => {
          if (dropdown) {
            dropdown.focus();
          }
        });
      }
    },
  },
  created() {
    this.handleEscapeKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        this.isModalOpen = false;
      }
    };
    document.addEventListener('keydown', this.handleEscapeKey);
  },
  mounted() {
    this.$store.dispatch('navigation/startPolling');
  },
  beforeDestroy() {
    this.$store.dispatch('navigation/stopPolling');
    if (this.handleEscapeKey) {
      document.removeEventListener('keydown', this.handleEscapeKey);
    }
  },
  methods: {
    toggleModal() {
      this.isModalOpen = !this.isModalOpen;
    },
    handleClickOutside(target) {
      const desktopButton = document.getElementById('desktop-account-menu-btn');
      const mobileButton = document.getElementById('mobile-account-menu-btn');
      if (this.isModalOpen) {
        if (!(desktopButton?.contains(target) || mobileButton?.contains(target))) {
          this.isModalOpen = false;
        }
      }
    },
    upgradeClicked() {
      sendSegmentEvent('GlobalNav_UpgradePricing', {});
    },
  },
};
</script>

<style lang="scss">
.nav-icon-group {
  &:focus {
    border: 2px dotted #bb0049;
    outline: none;
  }
  border: 2px solid var(--light-surface-foreground, #fff);
  width: 80px;
  &:hover {
    border-color: #f3f3f3;
    cursor: pointer;
    background-color: #f3f3f3;
    p {
      color: #bb0049;
    }
    span {
      color: #bb0049;
    }
    path {
      fill: #bb0049;
    }
  }
  &.active:not(:focus) {
    border-bottom: 2px solid var(--on-surface-headers, #05070f);
    &.mobile {
      border-top: 2px solid var(--on-surface-headers, #05070f);
      border-bottom: 2px solid #fff;
      &:focus {
        border-bottom: 2px dotted #bb0049;
      }
      &:hover {
        border-top-color: #bb0049;
        border-bottom-color: #f3f3f3;
      }
    }
    &:hover {
      border-bottom-color: #bb0049;
    }
  }
}

.global-navigation {
  z-index: 100;
  position: fixed;
  top: 0;
  width: 100%;
  background: var(--light-surface-foreground, #fff);
  display: flex;
  align-items: center;
  height: 48px;
  border-bottom: none;
  box-shadow: 0 2px 12px rgba(215, 216, 219, 0.9);

  @media only screen and (min-width: 768px) {
    height: 80px;
    border-bottom: 1px solid #d7d8d9;
    box-shadow: none;
  }

  .icon-container {
    gap: 12px;
    @media only screen and (min-width: 768px) {
      display: flex;
    }
    @media only screen and (min-width: 1024px) {
      gap: 32px;
    }
  }
}

.icon-container-mobile-bottom {
  z-index: 100;
  justify-content: space-around;
  height: 80px;
  width: 100vw;
  position: fixed;
  bottom: 0;
  background-color: var(--light-surface-foreground, #fff);
  @media only screen and (min-width: 768px) {
    visibility: hidden;
  }
  .nav-icon-group {
    width: 72px;
  }
}

.upside-down-icon {
  transform: rotate(180deg);
}

.truncate-name {
  max-width: 64px;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  display: inline-block;
}

.mobile-messaging-icon {
  @media only screen and (min-width: 768px) {
    display: none;
  }
  &:focus {
    outline: 2px dotted #bb0049;
  }
  &:hover {
    cursor: pointer;
    path {
      fill: #bb0049;
    }
  }
}

// this is for the cookie banner icon which overlaps mobile bottom nav otherwise
#ot-sdk-btn-floating.ot-floating-button {
  bottom: 85px !important;
  z-index: 51 !important;
  @media only screen and (min-width: 768px) {
    bottom: 10px !important;
  }
}

// this is for the zendesk support icon which overlaps mobile bottom nav otherwise
@media (max-width: 767px) {
  #launcher {
    margin-bottom: 85px !important;
  }
}
</style>
